/**
 * CarouselController
 * Manages carousel timing, state, and slide progression
 */

export interface CarouselState {
  currentSlide: number;
  totalSlides: number;
  isRunning: boolean;
  progress: number; // 0-1, representing countdown progress
}

export type SlideChangeCallback = (state: CarouselState) => void;

export class CarouselController {
  private currentSlide = 0;
  private totalSlides = 0;
  private isRunning = false;
  private progressIntervalId: ReturnType<typeof setInterval> | null = null;
  private progress = 0;
  private readonly intervalMs: number;
  private readonly onSlideChange: SlideChangeCallback;
  private readonly progressUpdateMs = 50; // Update progress every 50ms for smooth animation

  constructor(intervalMs: number, onSlideChange: SlideChangeCallback) {
    this.intervalMs = intervalMs;
    this.onSlideChange = onSlideChange;
  }

  /**
   * Initialize the carousel with a specific number of slides
   */
  init(totalSlides: number): void {
    this.totalSlides = totalSlides;
    this.currentSlide = 0;
    this.notifyChange();
  }

  /**
   * Start auto-advancing slides
   */
  start(): void {
    if (this.isRunning || this.totalSlides <= 1) {
      return;
    }

    this.isRunning = true;
    this.progress = 0;

    // Progress update interval for smooth animation
    this.progressIntervalId = setInterval(() => {
      this.progress += this.progressUpdateMs / this.intervalMs;
      if (this.progress >= 1) {
        this.progress = 0;
        this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
      }
      this.notifyChange();
    }, this.progressUpdateMs);

    this.notifyChange();
  }

  /**
   * Stop auto-advancing slides
   */
  stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;
    if (this.progressIntervalId !== null) {
      clearInterval(this.progressIntervalId);
      this.progressIntervalId = null;
    }
    this.progress = 0;
    this.notifyChange();
  }

  /**
   * Advance to the next slide (loops back to first after last)
   */
  next(): void {
    if (this.totalSlides === 0) {
      return;
    }

    this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
    this.notifyChange();
  }

  /**
   * Go to a specific slide
   */
  goTo(slideIndex: number): void {
    if (slideIndex < 0 || slideIndex >= this.totalSlides) {
      return;
    }

    this.currentSlide = slideIndex;
    this.notifyChange();
  }

  /**
   * Get the current state
   */
  getState(): CarouselState {
    return {
      currentSlide: this.currentSlide,
      totalSlides: this.totalSlides,
      isRunning: this.isRunning,
      progress: this.progress,
    };
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stop();
  }

  private notifyChange(): void {
    this.onSlideChange(this.getState());
  }
}
